import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Persisted view-mode preference for the Contacts list (CLAUDE.md §2.3).
 *
 * Search / filter live in the URL-bound page state and stay out of the store —
 * those are server-driven query params, not durable user prefs.
 */
export type ContactsViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const CONTACTS_VIEW_MODES: ContactsViewMode[] = ['table', 'list', 'cards', 'grouped'];

interface ContactsUiState {
  view: ContactsViewMode;
  setView: (view: ContactsViewMode) => void;
  /** Transient toggle — refreshes default to the active list. */
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-created`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
  /** Persisted tag filter — survives reloads. */
  tag: string;
  setTag: (tag: string) => void;
}

export const useContactsUi = create<ContactsUiState>()(
  persist(
    (set) => ({
      view: 'table',
      setView: (view) => set({ view }),
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      tag: 'all',
      setTag: (tag) => set({ tag }),
    }),
    {
      name: 'crm.contacts:ui',
      // View mode + tag filter persist; the archived toggle and ordering stay
      // ephemeral (refreshes default to a clean state).
      partialize: (s) => ({ view: s.view, tag: s.tag }),
    },
  ),
);
